import type { EventCapturer } from '#src/analytics/analytics_event.ts';
import { useGetOwnWishlist } from '#src/pages/group/own_wishlist_card/use_get_own_wishlist.ts';
import { WishlistItemDialog } from '#src/pages/group/own_wishlist_card/wishlist_item_dialog.tsx';
import { WISHLIST_ITEM_LIMIT } from '#src/pages/group/own_wishlist_card/wishlist_item_form.ts';
import type { WishlistClient } from '#src/wishlists/http/wishlist_client.ts';
import type { WishlistItem } from '@packages/contracts/wishlist';
import { exists } from '@packages/primitives/exists';
import { Alert } from '@packages/ui/base/alert/alert';
import { Badge } from '@packages/ui/base/badge/badge';
import { Button } from '@packages/ui/base/button/button';
import { Card } from '@packages/ui/base/card/card';
import { Tooltip } from '@packages/ui/base/tooltip/tooltip';
import { ArrowUpRightIcon, GiftIcon, PencilSimpleLineIcon, PlusIcon } from '@phosphor-icons/react';
import { useState } from 'react';

const ITEM_COUNT_VISIBILITY_THRESHOLD = 20;
const EDIT_ITEM_LABEL = 'Edit item';
const EDIT_BUTTON_TOOLTIP_DELAY_DURATION = 50;

export interface OwnWishlistCardProps {
  captureEvent: EventCapturer;
  wishlistClient: WishlistClient;
  groupId: string;
  participantId: string;
}

export const OwnWishlistCard = ({
  wishlistClient,
  captureEvent,
  groupId,
  participantId,
}: OwnWishlistCardProps) => {
  const getOwnWishlist = useGetOwnWishlist(wishlistClient, { groupId, participantId });
  const dialog = useWishlistDialog();

  const items = getOwnWishlist.items;
  const canAddItem = items.length < WISHLIST_ITEM_LIMIT;

  const showError = getOwnWishlist.hasError;
  const showLoading = !showError && getOwnWishlist.isPending && !exists(getOwnWishlist.result);
  const showEmptyState = !showError && !showLoading && items.length === 0;
  const showItems = !showError && !showLoading && items.length > 0;

  return (
    <Card className="py-6">
      {/* ---- Header ---- */}
      <div className="mb-5 flex items-center gap-3">
        <h2 className="text-xl font-bold">Your wishlist</h2>
        {items.length >= ITEM_COUNT_VISIBILITY_THRESHOLD && (
          <ItemCountBadge count={items.length} limit={WISHLIST_ITEM_LIMIT} />
        )}
      </div>

      {/* ---- Content ---- */}
      {showError && (
        <RetryState
          message={getOwnWishlist.displayErrorMessage}
          onRetry={() => {
            void getOwnWishlist.refetch();
          }}
          isRetrying={getOwnWishlist.isFetching}
        />
      )}

      {showLoading && <LoadingState />}

      {showEmptyState && <EmptyState onAdd={dialog.openCreate} />}

      {showItems && (
        <Tooltip.Provider>
          <div className="flex flex-col gap-3">
            {items.map(item => (
              <WishlistItemRow key={item.id} item={item} onEdit={() => dialog.openEdit(item)} />
            ))}
          </div>
          {canAddItem && (
            <div className="mt-4 flex justify-end">
              <Button variant="primary" size="xs" onClick={dialog.openCreate}>
                <PlusIcon className="mr-1 size-4" weight="bold" />
                <span>Add item</span>
              </Button>
            </div>
          )}
        </Tooltip.Provider>
      )}

      <WishlistItemDialog
        wishlistClient={wishlistClient}
        captureEvent={captureEvent}
        open={dialog.open}
        item={dialog.editingItem}
        groupId={groupId}
        participantId={participantId}
        currentItemCount={items.length}
        onOpenChange={dialog.onOpenChange}
      />
    </Card>
  );
};

const ItemCountBadge = ({ count, limit }: { count: number; limit: number }) => {
  return (
    <Badge className="bg-amber-50 text-amber-700">
      {count} / {limit}
    </Badge>
  );
};

const EmptyState = ({ onAdd }: { onAdd: () => void }) => {
  return (
    <div className="flex flex-col items-center justify-center py-6 text-center">
      <span
        aria-hidden
        className="bg-primary/10 text-primary mb-4 inline-flex size-14 items-center justify-center rounded-full"
      >
        <GiftIcon className="size-7" weight="duotone" />
      </span>
      <p className="mb-1 font-semibold text-zinc-900">No items yet</p>
      <p className="text-muted-foreground mb-6 max-w-sm text-sm">
        Add gift ideas so your secret santa knows exactly what to get you.
      </p>
      <Button onClick={onAdd}>
        <PlusIcon className="mr-1 size-4" weight="bold" />
        <span>Add your first item</span>
      </Button>
    </div>
  );
};

const LoadingState = () => {
  return (
    <div aria-label="Loading wishlist items">
      <div className="mb-3 h-16 animate-pulse rounded-lg bg-zinc-100" />
      <div className="h-16 animate-pulse rounded-lg bg-zinc-100" />
    </div>
  );
};

const RetryState = ({
  isRetrying,
  message,
  onRetry,
}: {
  isRetrying: boolean;
  message?: string;
  onRetry: () => void;
}) => {
  return (
    <div className="rounded-lg border border-zinc-100 bg-zinc-50/80 p-4">
      <Alert variant="error" className="mb-4">
        {message}
      </Alert>
      <Button
        variant="secondary"
        size="sm"
        onClick={onRetry}
        loading={isRetrying}
        disabled={isRetrying}
      >
        <span>Retry</span>
      </Button>
    </div>
  );
};

/**
 * Separates dialog open/close state from the editing item so that
 * closing the dialog does not flash the form to "create" mode during
 * the close animation.
 */
function useWishlistDialog() {
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WishlistItem | null>(null);

  return {
    open,
    editingItem,
    openCreate: () => {
      setEditingItem(null);
      setOpen(true);
    },
    openEdit: (item: WishlistItem) => {
      setEditingItem(item);
      setOpen(true);
    },
    onOpenChange: (nextOpen: boolean) => {
      if (!nextOpen) {
        setOpen(false);
      }
    },
  };
}

const WishlistItemRow = ({ item, onEdit }: { item: WishlistItem; onEdit: () => void }) => {
  const hasUrl = exists(item.url);

  return (
    <div className="bg-primary/6 ring-primary/30 flex flex-col rounded-2xl px-6 py-4 ring-1">
      <div className={`flex gap-4 ${hasUrl ? 'items-start' : 'items-center'}`}>
        <div className="min-w-0 flex-1">
          {/* ---- Title ---- */}
          <p className="line-clamp-2 font-bold">{item.title}</p>

          {/* ---- URL ---- */}
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/65 mt-1 inline-flex max-w-full items-center gap-1 text-sm underline-offset-2 hover:underline"
            >
              <span className="truncate">{getDisplayHost(item.url) ?? 'Link'}</span>
              <ArrowUpRightIcon className="size-3.5 shrink-0" />
            </a>
          )}
        </div>

        <EditButton onClick={onEdit} />
      </div>

      {/* ---- Note ---- */}
      {item.note && (
        <p
          className={`text-foreground/60 line-clamp-3 text-sm wrap-break-word whitespace-pre-wrap ${hasUrl ? 'mt-3' : 'mt-1'}`}
        >
          {item.note}
        </p>
      )}
    </div>
  );
};

const EditButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Tooltip.Root delayDuration={EDIT_BUTTON_TOOLTIP_DELAY_DURATION}>
      <Tooltip.Trigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClick}
          aria-label={EDIT_ITEM_LABEL}
          className="hover:bg-primary/20 shrink-0"
        >
          <PencilSimpleLineIcon className="size-4" />
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content>{EDIT_ITEM_LABEL}</Tooltip.Content>
    </Tooltip.Root>
  );
};

function getDisplayHost(url: string): string | null {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.startsWith('www.') ? host.slice(4) : host;
  } catch {
    return null;
  }
}
